/**
 * The Trust Centre — a PUBLIC page. It must render with NO session.
 *
 * WHO IS LOOKING AT THIS
 * A prospect, an auditor or a partner who was sent a link and wants a credible,
 * current answer to "how seriously does this organisation take security?"
 * without a two-week e-mail chain. They have no account here and never will.
 * Everything below follows from that:
 *
 *   * NO APP SHELL, NO NAVIGATION, NO SIGN-IN. There is nothing else for them
 *     here, and a sign-in box on a page for strangers is a dead end.
 *   * IT USES `fetch` DIRECTLY, NOT THE `api` CLIENT. The client attaches the
 *     bearer token and, on a 401, tries to refresh and tear down a session. A
 *     public page has no business sending a visitor's token to a public
 *     endpoint, and nothing about it should ever involve their session.
 *   * IT DISPLAYS WHAT THE API SENT AND NOTHING ELSE. The API returns an
 *     allow-listed view (see apps/api/src/modules/trust/service.ts); this page
 *     renders those fields as plain text. No HTML from the response is ever
 *     interpreted — React escapes it — because the organisation wrote some of it.
 *   * ONE FAILURE MESSAGE. Unknown link, withdrawn page, replaced link: the API
 *     answers them all identically (404) and so does this page.
 *   * IT IS HONEST ABOUT WHAT IT IS. The figures are the organisation's own
 *     self-assessment, not an audit or a certificate, and the page says so.
 *
 * This file also exports `TrustCentreView` (the presentational part), which the
 * administrator's settings screen renders for its preview — so the preview is the
 * same code as the page, not a look-alike.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Field, Pill, ProgressBar, Skeleton, ErrorNotice, type Tone } from '../components/ui';
import { IconShield, IconFramework, IconPolicy, IconScan } from '../components/icons';
import { useTrustPortalT } from '../lib/trustPortalStrings';

// ---------------------------------------------------------------------------
//  Strings
//
//  Every string, with its English text, so both screens render correctly before
//  the locale files are updated. Keys are the i18n keys; `{{x}}` is i18next
//  interpolation; `_one` / `_other` are i18next plural forms for `count`.
// ---------------------------------------------------------------------------

export const TRUST_STRINGS = {
  // ---- Public page ----
  'trust.public.kicker': 'Trust Centre',
  'trust.public.tagline': 'Security and compliance overview',
  'trust.public.unavailableTitle': 'This Trust Centre is not available',
  'trust.public.unavailableBody': 'The link may be mistyped, may have been replaced with a newer one, or the organisation may have withdrawn the page. Ask whoever sent it to you for the current link.',
  'trust.public.loadFailed': 'The page could not be loaded. Check your connection and try again.',
  'trust.public.retry': 'Try again',
  'trust.public.empty': 'This organisation has not published any details yet.',
  'trust.public.frameworks': 'Assessed against',
  'trust.public.frameworksHint': 'The overall result of the organisation’s most recent completed self-assessment against each framework.',
  'trust.public.overall': 'Overall compliance',
  'trust.public.assessmentFinalised': 'Assessment completed',
  'trust.public.assessmentInProgress': 'Assessment in progress',
  'trust.public.finalisedOn': 'Completed {{date}}',
  'trust.public.inProgressNote': 'No result is published until the assessment is complete.',
  'trust.public.policies': 'Policies',
  'trust.public.policiesHint': 'Policy names and approval status only. The policy documents themselves are not published.',
  'trust.public.policyApproved': 'Approved',
  'trust.public.policyInReview': 'In review',
  'trust.public.policyNotApproved': 'Not yet approved',
  'trust.public.approvedOn': 'Approved {{date}}',
  'trust.public.scan': 'External security scan',
  'trust.public.scanBody': 'The organisation’s internet-facing systems were last scanned on {{date}}.',
  'trust.public.scanNote': 'The results of the scan are not published.',
  'trust.public.contact': 'Security questions',
  'trust.public.contactBody': 'For questions about this organisation’s security, contact:',
  'trust.public.disclaimer': 'Published by {{name}}. These figures come from the organisation’s own self-assessment in OpenComply. They are not an independent audit or a certification.',
  'trust.public.asOf': 'Page generated {{date}}.',

  // ---- Settings screen ----
  'trust.settings.title': 'Trust Centre',
  'trust.settings.subtitle': 'A public page that shows your customers, auditors and partners where your security stands.',
  'trust.settings.adminOnly': 'Only an administrator of this organisation can change what it publishes.',
  'trust.settings.externalReadOnly': 'You are managing this organisation on its behalf. Only the organisation’s own administrators can change what it publishes.',
  'trust.settings.loadFailed': 'The Trust Centre settings could not be loaded.',
  'trust.settings.warningTitle': 'Everything you tick here is public',
  'trust.settings.warningBody': 'Anyone who has the link can read it without signing in. Nothing is published until you switch the page on.',
  'trust.settings.neverTitle': 'Never published, whatever you choose',
  'trust.settings.neverBody': 'Gaps and their counts, evidence files and links, vendors and their scores, users and e-mail addresses, remediation plans, scan findings, credits and billing, and your place in a partner channel.',
  'trust.settings.publishLabel': 'Publish my Trust Centre',
  'trust.settings.publishHint': 'Off by default. While it is off, the link shows nothing at all — it looks exactly like a link that never existed.',
  'trust.settings.statePublished': 'Published',
  'trust.settings.stateNotPublished': 'Not published',
  'trust.settings.linkTitle': 'Public link',
  'trust.settings.linkNone': 'Your link is created the first time you save these settings.',
  'trust.settings.linkOffNote': 'The link exists but shows nothing while the page is switched off.',
  'trust.settings.copy': 'Copy link',
  'trust.settings.copied': 'Copied',
  'trust.settings.copyFailed': 'Could not copy. Select the link and copy it by hand.',
  'trust.settings.open': 'Open',
  'trust.settings.rotate': 'Create a new link',
  'trust.settings.rotateTitle': 'Replace the public link?',
  'trust.settings.rotateBody': 'The current link stops working immediately and shows the same “not available” page as any unknown link. Anyone you gave it to will need the new one.',
  'trust.settings.rotateConfirm': 'Replace link',
  'trust.settings.aboutTitle': 'About your organisation',
  'trust.settings.displayName': 'Name shown on the page',
  'trust.settings.displayNameHint': 'Leave empty to use “{{name}}”.',
  'trust.settings.description': 'Short description',
  'trust.settings.descriptionHint': 'Plain text, written for this page. Your internal organisation profile is never used.',
  'trust.settings.descriptionCount': '{{used}} of {{max}} characters',
  'trust.settings.contact': 'Security contact e-mail',
  'trust.settings.contactHint': 'Shown publicly so people can ask security questions. Use a shared mailbox, not a person’s address.',
  'trust.settings.contactBad': 'Enter a valid e-mail address, or leave it empty.',
  'trust.settings.frameworksTitle': 'Frameworks',
  'trust.settings.frameworksHint': 'Tick a framework to show its overall percentage once an assessment is completed. A percentage is never shown for an assessment that is still in progress.',
  'trust.settings.frameworksNone': 'You have not started an assessment against any framework yet.',
  'trust.settings.frameworksSelected_one': '{{count}} framework selected',
  'trust.settings.frameworksSelected_other': '{{count}} frameworks selected',
  'trust.settings.frameworkFinalised': '{{percent}}% — completed {{date}}',
  'trust.settings.frameworkInProgress': 'In progress — no percentage is published',
  'trust.settings.policiesTitle': 'Policies',
  'trust.settings.policiesHint': 'Tick a policy to show its name and approval status. Policy documents are never published. Untick any policy whose name or status you would rather not show.',
  'trust.settings.policiesNone': 'You have no active policies yet.',
  'trust.settings.policiesSelected_one': '{{count}} policy selected',
  'trust.settings.policiesSelected_other': '{{count}} policies selected',
  'trust.settings.scanTitle': 'External security scan',
  'trust.settings.scanLabel': 'Show that an external scan was completed, and when',
  'trust.settings.scanHint': 'Only the date is published. What the scan found is never published.',
  'trust.settings.scanLast': 'Last completed scan: {{date}}',
  'trust.settings.scanNone': 'No completed scan yet, so there is nothing to show.',
  'trust.settings.previewTitle': 'What the public sees',
  'trust.settings.previewHint': 'Built by the same code as the public page, from your saved settings.',
  'trust.settings.previewStale': 'You have unsaved changes. This preview shows the last saved settings.',
  'trust.settings.previewOff': 'The page is switched off, so the public currently sees nothing. This is what it would show once you publish.',
  'trust.settings.save': 'Save settings',
  'trust.settings.saving': 'Saving…',
  'trust.settings.saved': 'Saved.',
  'trust.settings.unsaved': 'Unsaved changes',
  'trust.settings.confirmTitle': 'Publish to the internet?',
  'trust.settings.confirmBody': 'Anyone with the link will be able to see the details listed in the preview, without signing in. You can switch the page off, or replace the link, at any time.',
  'trust.settings.confirmPublish': 'Publish',
  'trust.settings.cancel': 'Cancel',
  'trust.settings.error.TRUST_CENTRE_INVALID_SELECTION': 'One of the selected items is no longer available. Reload the page and choose again.',
  'trust.settings.error.TRUST_CENTRE_EXTERNAL_FORBIDDEN': 'Only the organisation’s own administrators can change what it publishes.',
  'trust.settings.error.TRUST_CENTRE_NOT_CONFIGURED': 'Save the settings once before creating a new link.',
  'trust.settings.error.TRUST_CENTRE_SLUG_UNAVAILABLE': 'A new link could not be created. Try again.',
  'trust.settings.error.VALIDATION_FAILED': 'Some of the details are not valid. Check the fields and try again.',
  'trust.settings.error.INSUFFICIENT_PERMISSIONS': 'Only an administrator can change these settings.',
  'trust.settings.error.UNKNOWN': 'Something went wrong. Try again.',
} as const;

type StringKey = keyof typeof TRUST_STRINGS;
/** A key as callers write it: plural forms are addressed by their base key. */
export type TrustStringKey = StringKey extends infer K ? (K extends `${infer Base}_${'one' | 'other'}` ? Base : K) : never;

/**
 * Translate with the built-in English as the fallback.
 *
 * For a key that has plural forms, pass `count` and the `_one` / `_other`
 * defaults are supplied for i18next to choose between.
 */
export function useTrustStrings() {
  const { t, i18n } = useTranslation();
  const tx = (key: TrustStringKey, options: Record<string, unknown> = {}): string => {
    const strings: Record<string, string | undefined> = TRUST_STRINGS;
    const one = strings[`${key}_one`];
    const other = strings[`${key}_other`];
    return t(key, {
      defaultValue: strings[key],
      ...(one !== undefined ? { defaultValue_one: one } : {}),
      ...(other !== undefined ? { defaultValue_other: other } : {}),
      ...options,
    });
  };
  const formatDate = (iso: string | null | undefined): string =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(iso))
      : '';
  return { tx, formatDate, locale: i18n.language };
}

// ---------------------------------------------------------------------------
//  The public response
//
//  Mirrors the API's allow-list (publicTrustViewSchema). There is deliberately no
//  id anywhere in it. Every section is optional: an unpublished section is
//  simply absent.
// ---------------------------------------------------------------------------

export interface PublicTrustView {
  name: string;
  description: string | null;
  asOf: string;
  frameworks?: Array<{
    name: string;
    version: string;
    status: 'FINALIZED' | 'IN_PROGRESS';
    compliancePercent: number | null;
    finalizedAt: string | null;
  }>;
  policies?: Array<{
    name: string;
    approvalStatus: 'APPROVED' | 'IN_REVIEW' | 'NOT_APPROVED';
    approvedAt: string | null;
  }>;
  externalScan?: { lastCompletedAt: string };
  contact?: { email: string };
}

const POLICY_TONE: Record<'APPROVED' | 'IN_REVIEW' | 'NOT_APPROVED', Tone> = {
  APPROVED: 'good',
  IN_REVIEW: 'warning',
  NOT_APPROVED: 'neutral',
};

/** A tone for a percentage. Colour reinforces the number, which is always printed. */
function toneForPercent(percent: number): Tone {
  if (percent >= 80) return 'good';
  if (percent >= 50) return 'warning';
  return 'serious';
}

// ---------------------------------------------------------------------------
//  Presentational view — shared with the settings preview
// ---------------------------------------------------------------------------

export function TrustCentreView({ view }: { view: PublicTrustView }) {
  const { tx, formatDate } = useTrustStrings();

  const hasSections = Boolean(view.frameworks?.length || view.policies?.length || view.externalScan || view.contact);

  return (
    <div className="space-y-4">
      <header className="oc-card p-5 sm:p-7">
        <div className="flex items-start gap-3 sm:gap-4">
          <span className="shrink-0 grid place-items-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-brand/10 text-brand">
            <IconShield className="w-6 h-6" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
              {tx('trust.public.kicker')} · {tx('trust.public.tagline')}
            </p>
            {/* break-words: an organisation name has no obligation to be short. */}
            <h1 className="oc-h1 mt-1 text-balance break-words">{view.name}</h1>
          </div>
        </div>
        {view.description && (
          // whitespace-pre-line keeps the paragraph breaks the author typed. The
          // text is rendered as text — never as HTML.
          <p className="mt-4 text-ink-secondary whitespace-pre-line break-words">{view.description}</p>
        )}
      </header>

      {!hasSections && (
        <Card>
          <p className="text-center text-ink-secondary py-6">{tx('trust.public.empty')}</p>
        </Card>
      )}

      {view.frameworks && view.frameworks.length > 0 && (
        <Card>
          <CardHeader
            title={<span className="inline-flex items-center gap-2"><IconFramework className="w-5 h-5 text-ink-muted" />{tx('trust.public.frameworks')}</span>}
            subtitle={tx('trust.public.frameworksHint')}
          />
          <ul className="space-y-3">
            {view.frameworks.map((f) => (
              <li key={`${f.name}|${f.version}`} className="rounded-xl border border-line p-4">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink break-words">{f.name}</p>
                    <p className="text-xs text-ink-muted break-words">{f.version}</p>
                  </div>
                  <Pill tone={f.status === 'FINALIZED' ? 'good' : 'neutral'}>
                    {f.status === 'FINALIZED' ? tx('trust.public.assessmentFinalised') : tx('trust.public.assessmentInProgress')}
                  </Pill>
                </div>
                {f.status === 'FINALIZED' && f.compliancePercent !== null ? (
                  <div className="mt-3">
                    <p className="text-xs text-ink-secondary mb-1">{tx('trust.public.overall')}</p>
                    <ProgressBar
                      value={f.compliancePercent}
                      tone={toneForPercent(f.compliancePercent)}
                      label={`${f.name}: ${tx('trust.public.overall')}`}
                    />
                    {f.finalizedAt && (
                      <p className="mt-1.5 text-xs text-ink-muted">{tx('trust.public.finalisedOn', { date: formatDate(f.finalizedAt) })}</p>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-ink-muted">{tx('trust.public.inProgressNote')}</p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {view.policies && view.policies.length > 0 && (
        <Card>
          <CardHeader
            title={<span className="inline-flex items-center gap-2"><IconPolicy className="w-5 h-5 text-ink-muted" />{tx('trust.public.policies')}</span>}
            subtitle={tx('trust.public.policiesHint')}
          />
          <ul className="divide-y divide-line">
            {view.policies.map((p) => (
              <li key={p.name} className="py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                <span className="min-w-0 break-words text-ink">{p.name}</span>
                <span className="flex items-center gap-2">
                  {p.approvalStatus === 'APPROVED' && p.approvedAt && (
                    <span className="text-xs text-ink-muted">{tx('trust.public.approvedOn', { date: formatDate(p.approvedAt) })}</span>
                  )}
                  <Pill tone={POLICY_TONE[p.approvalStatus]}>
                    {p.approvalStatus === 'APPROVED' ? tx('trust.public.policyApproved')
                      : p.approvalStatus === 'IN_REVIEW' ? tx('trust.public.policyInReview')
                      : tx('trust.public.policyNotApproved')}
                  </Pill>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {view.externalScan && (
        <Card>
          <CardHeader
            title={<span className="inline-flex items-center gap-2"><IconScan className="w-5 h-5 text-ink-muted" />{tx('trust.public.scan')}</span>}
          />
          <p className="text-ink">{tx('trust.public.scanBody', { date: formatDate(view.externalScan.lastCompletedAt) })}</p>
          <p className="mt-1 text-sm text-ink-muted">{tx('trust.public.scanNote')}</p>
        </Card>
      )}

      {view.contact && (
        <Card>
          <CardHeader title={tx('trust.public.contact')} />
          <p className="text-ink-secondary">{tx('trust.public.contactBody')}</p>
          {/* The address is shown as text AND linked: some people cannot open mailto: links. */}
          <p className="mt-1 break-all">
            <a className="font-medium text-brand hover:underline" href={`mailto:${view.contact.email}`}>{view.contact.email}</a>
          </p>
        </Card>
      )}

      <footer className="px-1 pb-6 text-xs text-ink-muted space-y-1">
        <p>{tx('trust.public.disclaimer', { name: view.name })}</p>
        <p>{tx('trust.public.asOf', { date: formatDate(view.asOf) })}</p>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Request access + portal sign-in — the gateway to the "answer-once" portal
// ---------------------------------------------------------------------------

/**
 * A self-service access-request form, shown on the public page. Posts to the
 * public, rate-limited endpoint with `fetch` (never the tenant `api` client),
 * and ALWAYS reports success: the server deliberately reveals nothing about the
 * outcome, so neither does this form.
 */
function RequestAccessCard({ slug }: { slug: string }) {
  const { tx } = useTrustPortalT();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState('sending');
    try {
      const response = await fetch(`/api/public/trust/${encodeURIComponent(slug)}/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          company: company.trim() || null,
          reason: reason.trim() || null,
        }),
      });
      // 202 = received. A 400 is a malformed body (e.g. a bad e-mail); anything
      // else, including an unknown slug, still looks like success to the caller.
      setState(response.status === 400 ? 'failed' : 'sent');
    } catch {
      setState('failed');
    }
  };

  const portalUrl = `${window.location.origin}/trust/${slug}/portal`;

  if (state === 'sent') {
    return (
      <Card>
        <CardHeader title={tx('trust.request.title')} />
        <p className="text-good" role="status">{tx('trust.request.sent')}</p>
        <p className="mt-2 text-sm"><a className="text-brand hover:underline" href={portalUrl}>{tx('trust.request.open')}</a></p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={tx('trust.request.title')} subtitle={tx('trust.request.body')} />
      <form onSubmit={submit} className="space-y-1">
        <Field label={tx('trust.request.email')} htmlFor="req-email">
          <input id="req-email" type="email" required className="oc-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={tx('trust.request.company')} htmlFor="req-company">
          <input id="req-company" className="oc-input" value={company} onChange={(e) => setCompany(e.target.value)} />
        </Field>
        <Field label={tx('trust.request.reason')} htmlFor="req-reason">
          <textarea id="req-reason" rows={3} maxLength={2000} className="oc-input py-2 min-h-[4rem]" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {state === 'failed' && <p className="text-sm text-critical" role="alert">{tx('trust.request.failed')}</p>}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button type="submit" variant="primary" disabled={state === 'sending' || email.trim() === ''}>
            {state === 'sending' ? tx('trust.request.submitting') : tx('trust.request.submit')}
          </Button>
          <a className="text-sm text-brand hover:underline" href={portalUrl}>{tx('trust.request.open')}</a>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------

/** Raised for a 404 so the page can tell "not available" from "could not load". */
class NotAvailable extends Error {}

/** The slug as the API accepts it; anything else is not worth a request. */
const SLUG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

async function fetchPublicView(slug: string, signal: AbortSignal): Promise<PublicTrustView> {
  if (slug.length < 3 || slug.length > 64 || !SLUG_SHAPE.test(slug)) throw new NotAvailable();

  const response = await fetch(`/api/public/trust/${encodeURIComponent(slug)}`, {
    method: 'GET',
    // No cookies, no token: this page is anonymous by construction.
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    cache: 'no-store',
    signal,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 404) throw new NotAvailable();
  if (!response.ok) throw new Error('trust-centre-load-failed');
  return (await response.json()) as PublicTrustView;
}

export function TrustCentrePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { tx } = useTrustStrings();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['public-trust-centre', slug],
    queryFn: ({ signal }) => fetchPublicView(slug, signal),
    // A refused link would only be refused again; a network blip is worth one retry.
    retry: (count, err) => !(err instanceof NotAvailable) && count < 1,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // This page is shared by link, not discovered by search. The API says so in its
  // headers; the document says so too, for crawlers that read it from the HTML.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const before = document.title;
    return () => {
      meta.remove();
      document.title = before;
    };
  }, []);

  useEffect(() => {
    document.title = data ? `${data.name} — ${tx('trust.public.kicker')}` : tx('trust.public.kicker');
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-dvh bg-surface-sunken">
      <main className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
        {isLoading && (
          <div className="space-y-4" aria-busy="true">
            <Skeleton className="h-32" />
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
        )}

        {error instanceof NotAvailable && (
          <div className="oc-card p-6 sm:p-8 text-center" role="alert">
            <span className="mx-auto grid place-items-center w-12 h-12 rounded-xl bg-ink/[0.06] text-ink-muted">
              <IconShield className="w-6 h-6" />
            </span>
            <h1 className="oc-h1 mt-4 text-balance">{tx('trust.public.unavailableTitle')}</h1>
            <p className="oc-muted mt-2 max-w-md mx-auto">{tx('trust.public.unavailableBody')}</p>
          </div>
        )}

        {error && !(error instanceof NotAvailable) && (
          <ErrorNotice
            message={tx('trust.public.loadFailed')}
            onRetry={() => void refetch()}
            retryLabel={tx('trust.public.retry')}
          />
        )}

        {data && (
          <>
            <TrustCentreView view={data} />
            <div className="mt-4">
              <RequestAccessCard slug={slug} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
